import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MealPlanDto, ReminderDto, SuggestedReminderDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { BellIcon } from '../icons';
import { Section } from '../Section';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { formatDayLong } from '../../lib/planDates';
import { planTitle, SLOT_LABEL } from '../../lib/plans';
import {
  formatReminderTime,
  fromDateTimeLocal,
  reminderGoogleUrl,
  scheduleKeys,
  toDateTimeLocal,
} from '../../lib/schedule';

const chip =
  'inline-flex min-h-11 items-center justify-center rounded-xl border border-line bg-surface px-3 text-sm font-medium text-brand transition-colors hover:bg-brand-soft disabled:opacity-50';

/**
 * Plan page: reminders for this meal (D28) — chiefly thawing. Freezer
 * ingredients are suggested one click away; any other ingredient or a
 * free-text reminder can be added too. Each reminder can be ticked off or
 * sent straight to Google Calendar (the subscribed feed lags by hours).
 */
export function RemindersSection({ plan }: { plan: MealPlanDto }) {
  const qc = useQueryClient();
  const listKey = scheduleKeys.planReminders(plan.id);
  const reminders = useQuery({
    queryKey: listKey,
    queryFn: () => api.reminders.list(plan.id),
  });
  const suggested = useQuery({
    queryKey: scheduleKeys.suggestedReminders(plan.id),
    queryFn: () => api.reminders.suggested(plan.id),
    enabled: plan.recipes.length > 0,
  });
  // Covers the list, the suggestions and the dashboard card (all under this prefix).
  const invalidate = () => qc.invalidateQueries({ queryKey: ['plans', 'reminders'] });

  const create = useMutation({
    mutationFn: (input: Parameters<typeof api.reminders.create>[1]) =>
      api.reminders.create(plan.id, input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (v: { id: string; input: Parameters<typeof api.reminders.update>[2] }) =>
      api.reminders.update(plan.id, v.id, v.input),
    onMutate: async ({ id, input }) => {
      // Optimistic done toggle so the checkbox responds instantly.
      if (input.done === undefined) return;
      await qc.cancelQueries({ queryKey: listKey });
      qc.setQueryData<{ reminders: ReminderDto[] }>(listKey, (old) =>
        old
          ? {
              reminders: old.reminders.map((r) =>
                r.id === id ? { ...r, doneAt: input.done ? new Date().toISOString() : null } : r,
              ),
            }
          : old,
      );
    },
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: (reminderId: string) => api.reminders.remove(plan.id, reminderId),
    onSuccess: invalidate,
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [extra, setExtra] = useState('');

  const addSuggestion = (s: SuggestedReminderDto) =>
    create.mutate({
      kind: 'thaw',
      ingredientId: s.ingredientId,
      text: s.text,
      remindAt: s.remindAt,
    });

  function onAddCustom(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const remindAt = fromDateTimeLocal(String(fd.get('remindAt') ?? ''));
    if (!remindAt) return;
    create.mutate(
      { kind: 'custom', text: String(fd.get('text') ?? ''), remindAt },
      { onSuccess: () => form.reset() },
    );
  }

  const list = reminders.data?.reminders ?? [];
  const suggestions = suggested.data?.suggestions ?? [];
  const frozen = suggestions.filter((s) => s.inFreezer);
  const others = suggestions.filter((s) => !s.inFreezer);
  const details = `${SLOT_LABEL[plan.slot]} on ${formatDayLong(plan.date)}: ${planTitle(plan)}`;
  const defaultCustomTime = suggestions[0]?.remindAt ?? suggested.data?.serveAt;

  return (
    <Section
      accent="orange"
      icon={<BellIcon className="size-5" />}
      title="Reminders"
      description="Get a nudge to move things from the freezer to the fridge in time."
    >
      {reminders.isError ? <Alert tone="error">{messageForError(reminders.error)}</Alert> : null}
      {update.isError ? <Alert tone="error">{messageForError(update.error)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}

      {list.length ? (
        <ul className="flex flex-col divide-y divide-line">
          {list.map((r) => (
            <li key={r.id} className="flex flex-col gap-2 py-2">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 size-5 shrink-0 accent-brand"
                  checked={r.doneAt !== null}
                  aria-label={`Done: ${r.text}`}
                  onChange={(e) => update.mutate({ id: r.id, input: { done: e.target.checked } })}
                />
                <div className="min-w-0 flex-1">
                  <p className={r.doneAt ? 'text-muted line-through' : 'font-medium'}>{r.text}</p>
                  <p className="text-xs text-muted">{formatReminderTime(r.remindAt)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pl-8">
                <a
                  href={reminderGoogleUrl(r, details)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={chip}
                >
                  Add to Google Calendar
                </a>
                <button
                  type="button"
                  className={chip}
                  onClick={() => setEditing(editing === r.id ? null : r.id)}
                  aria-expanded={editing === r.id}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={`${chip} text-fail`}
                  onClick={() => remove.mutate(r.id)}
                  disabled={remove.isPending}
                >
                  Delete
                </button>
              </div>
              {editing === r.id ? (
                <form
                  className="flex flex-col gap-2 pl-8 sm:flex-row sm:items-end"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    const remindAt = fromDateTimeLocal(String(fd.get('remindAt') ?? ''));
                    update.mutate(
                      {
                        id: r.id,
                        input: {
                          text: String(fd.get('text') ?? ''),
                          ...(remindAt ? { remindAt } : {}),
                        },
                      },
                      { onSuccess: () => setEditing(null) },
                    );
                  }}
                >
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    Reminder
                    <input
                      name="text"
                      defaultValue={r.text}
                      required
                      maxLength={200}
                      className="min-h-11 rounded-lg border border-line bg-surface px-3"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    When
                    <input
                      name="remindAt"
                      type="datetime-local"
                      defaultValue={toDateTimeLocal(r.remindAt)}
                      required
                      className="min-h-11 rounded-lg border border-line bg-surface px-3"
                    />
                  </label>
                  <button type="submit" className={chip} disabled={update.isPending}>
                    Save
                  </button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      ) : reminders.isSuccess ? (
        <p className="text-sm text-muted">No reminders for this meal yet.</p>
      ) : null}

      {frozen.length ? (
        <div className="flex flex-col gap-2 rounded-lg bg-accent-soft/60 p-3">
          <p className="text-sm font-medium">From your freezer</p>
          <ul className="flex flex-col gap-2">
            {frozen.map((s) => (
              <li key={s.ingredientId} className="flex items-center justify-between gap-3">
                <span className="min-w-0 text-sm">
                  <span className="block">{s.text}</span>
                  <span className="block text-xs text-muted">
                    {formatReminderTime(s.remindAt)}
                    {s.adjusted ? ' · moved to the evening before' : ''}
                  </span>
                </span>
                <button
                  type="button"
                  className={chip}
                  disabled={create.isPending}
                  onClick={() => addSuggestion(s)}
                >
                  Remind me
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {others.length ? (
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            const s = others.find((o) => o.ingredientId === extra);
            if (s) addSuggestion(s);
            setExtra('');
          }}
        >
          <label className="flex flex-1 flex-col gap-1 text-sm">
            Thaw something else
            <select
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              className="min-h-11 rounded-lg border border-line bg-surface px-3"
            >
              <option value="">Choose an ingredient…</option>
              {others.map((s) => (
                <option key={s.ingredientId} value={s.ingredientId}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className={chip} disabled={!extra || create.isPending}>
            Add thaw reminder
          </button>
        </form>
      ) : null}

      <details className="text-sm">
        <summary className="cursor-pointer py-3 font-medium text-brand">
          Add your own reminder
        </summary>
        <form className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={onAddCustom}>
          <label className="flex flex-1 flex-col gap-1">
            Reminder
            <input
              name="text"
              required
              maxLength={200}
              placeholder="Soak the beans"
              className="min-h-11 rounded-lg border border-line bg-surface px-3"
            />
          </label>
          <label className="flex flex-col gap-1">
            When
            <input
              key={defaultCustomTime ?? 'none'}
              name="remindAt"
              type="datetime-local"
              required
              defaultValue={defaultCustomTime ? toDateTimeLocal(defaultCustomTime) : ''}
              className="min-h-11 rounded-lg border border-line bg-surface px-3"
            />
          </label>
          <button type="submit" className={chip} disabled={create.isPending}>
            Add
          </button>
        </form>
      </details>
      {create.isError ? <Alert tone="error">{messageForError(create.error)}</Alert> : null}
    </Section>
  );
}
